/**
 * Loading, error, and empty states (SPEC §9.4).
 *
 * "States: skeleton on load, inline field errors for 422, retry on 5xx, and an
 * empty state that names the likely cause rather than a shrug."
 *
 * The empty state is the one that gets skipped and the one that matters. "No
 * data" tells a reader nothing; "No rows for this date range" tells them which
 * control to reach for.
 */

import { RefreshCw } from 'lucide-react'

import { ApiError } from '../lib/api'

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="h-12 animate-pulse rounded-[var(--radius-lg)] bg-[var(--color-rule)]/50"
        />
      ))}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null
  const message = error instanceof Error ? error.message : String(error)

  // A 4xx will not fix itself, so no retry button is offered for one.
  const canRetry = onRetry !== undefined && (apiError === null || apiError.isRetryable)

  return (
    <div
      role="alert"
      className="rounded-[var(--radius-lg)] border border-[var(--color-negative)]/40 bg-[var(--color-negative)]/5 p-4"
    >
      <p className="text-[length:var(--text-sm)] font-medium text-[var(--color-negative)]">
        {apiError?.isNotFound === true ? 'This report is not available to you.' : message}
      </p>
      {apiError !== null && !apiError.isNotFound && (
        <p className="numeric mt-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          {apiError.code}
        </p>
      )}
      {canRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-2 py-1 text-[length:var(--text-xs)]"
        >
          <RefreshCw aria-hidden className="size-3.5" />
          Try again
        </button>
      )}
    </div>
  )
}

export function EmptyState({ cause }: { cause: string }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-dashed border-[var(--color-rule)] p-8 text-center">
      <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">{cause}</p>
    </div>
  )
}
